import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Alert,
  Typography,
  Divider,
  Link,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  cancelCalendarEvent,
  cancelServiceRequestFromCalendar,
  fetchCalendarEventDetail,
} from "../api/calendarApi";
import {
  DEFAULT_SHOP_TIMEZONE,
  utcIsoToShopLocalInput,
} from "../utils/calendarTime";

/** Linked-job schedule edits await customer change-request workflow (follow-up story). */
const RESCHEDULE_DISABLED_REASON =
  "Schedule edit from calendar is temporarily disconnected — awaiting requirements for a customer-visible change request (approval) flow. Cancel still works.";

/**
 * TASK-522 — details + cancel; reschedule UI disabled until change-request workflow ships.
 */
export default function CalendarEventDialog({
  open,
  onClose,
  shopId,
  event,
  timeZone = DEFAULT_SHOP_TIMEZONE,
  onSaved,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [summary, setSummary] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !event?.id) {
      return;
    }
    setError("");
    setCancelReason("");
    setTitle(event.title || "");
    setDescription(event.description || "");
    setStartLocal(utcIsoToShopLocalInput(event.start_at, timeZone));
    setEndLocal(utcIsoToShopLocalInput(event.end_at, timeZone));
    setSummary(null);

    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      try {
        const data = await fetchCalendarEventDetail(shopId, event.id);
        if (cancelled) return;
        if (data.event) {
          setTitle(data.event.title || "");
          setDescription(data.event.description || "");
          setStartLocal(utcIsoToShopLocalInput(data.event.start_at, timeZone));
          setEndLocal(utcIsoToShopLocalInput(data.event.end_at, timeZone));
        }
        setSummary(data.service_request_summary || null);
      } catch (err) {
        if (!cancelled) {
          console.warn("Event detail load failed:", err);
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, event, shopId, timeZone]);

  const linkedSrId = event?.service_request_id || summary?.id || null;
  const isOrphan = !linkedSrId;

  const handleCancelEvent = async () => {
    setSaving(true);
    setError("");
    try {
      if (linkedSrId) {
        await cancelServiceRequestFromCalendar(linkedSrId, {
          shopId,
          reason: cancelReason.trim() || "Cancelled from calendar",
        });
      } else {
        await cancelCalendarEvent(shopId, event.id);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to cancel appointment");
    } finally {
      setSaving(false);
    }
  };

  const tzHint = `Times are in shop timezone (${timeZone})`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Appointment details</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">{RESCHEDULE_DISABLED_REASON}</Alert>
          {isOrphan && (
            <Alert severity="warning">
              Not linked to a service request — not tracked for sales/fees.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            {tzHint}
            {loadingDetail ? " · Loading details…" : ""}
          </Typography>

          {summary && (
            <Stack spacing={0.5} sx={{ bgcolor: "action.hover", p: 1.5, borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Service request #{summary.id}
              </Typography>
              <Typography variant="body2">
                Status: {summary.status || "—"}
              </Typography>
              {summary.customer_name && (
                <Typography variant="body2">Customer: {summary.customer_name}</Typography>
              )}
              {summary.customer_phone && (
                <Typography variant="body2">Phone: {summary.customer_phone}</Typography>
              )}
              {summary.customer_email && (
                <Typography variant="body2">Email: {summary.customer_email}</Typography>
              )}
              {summary.car_label && (
                <Typography variant="body2">Vehicle: {summary.car_label}</Typography>
              )}
              {summary.vin && (
                <Typography variant="body2">VIN: {summary.vin}</Typography>
              )}
              {summary.job_total != null && (
                <Typography variant="body2">
                  Job total (read-only): ${Number(summary.job_total).toFixed(2)}
                </Typography>
              )}
              <Link component={RouterLink} to="/mechanic-dashboard" underline="hover">
                Open Mechanics Dashboard
              </Link>
            </Stack>
          )}

          <Divider />

          <TextField label="Title" value={title} fullWidth InputProps={{ readOnly: true }} />
          <TextField
            label="Notes"
            value={description}
            fullWidth
            multiline
            minRows={2}
            InputProps={{ readOnly: true }}
            helperText="Notes only — work/services belong on the job (follow-up details viewer)."
          />
          <Tooltip title={RESCHEDULE_DISABLED_REASON}>
            <span>
              <TextField
                label="Start"
                type="datetime-local"
                value={startLocal}
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </span>
          </Tooltip>
          <Tooltip title={RESCHEDULE_DISABLED_REASON}>
            <span>
              <TextField
                label="End"
                type="datetime-local"
                value={endLocal}
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </span>
          </Tooltip>
          <TextField
            label="Cancel reason (for linked jobs)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            multiline
            minRows={1}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" disabled={saving} onClick={handleCancelEvent}>
          Cancel appointment
        </Button>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Tooltip title={RESCHEDULE_DISABLED_REASON}>
          <span>
            <Button variant="contained" disabled>
              Save reschedule (disconnected)
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
